// CustomerListTable.tsx

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  TablePagination,
  styled,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import QRCode from "react-qr-code";
import MovieIcon from "@mui/icons-material/Movie";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import sales_management_service from "../../services/sales_management_service";
import { useTranslation } from "react-i18next";

// Define the Customer interface
interface Customer {
  id: string; // id is a string in your data
  purchaseDate: string;
  customerName: string;
  email: string;
  purchaseReceipt: string;
  event_id: string;
  eventName_en: string;
}

// Define the GroupedCustomer interface for email grouping
interface GroupedCustomer {
  email: string;
  customerName: string;
  mostRecentPurchaseDate: string;
  orderNumbers: string[];
  orderIds: string[];
}

// Define the PurchaseDetail interface
interface PurchaseDetail {
  customer: string; // user_name
  salesId: string; // reserv_id
  movieName: string; // eventName_en or eventName_am
  movieSubtitle: string; // description_en or description_am
  movieImage: string; // eventImage
  tickets: string[]; // tickets.split(',')
  ticketCount: number; // Number(sold_ticket)
  time: string; // dateTime.split(' ')[1]
  date: string; // dateTime.split(' ')[0]
  qr_url: string; // qr_url
  hasSeatmap: boolean;
}

// Styled Back Button with only back arrow icon
const BackButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#4D67EB",
  color: "#FFFFFF",
  padding: theme.spacing(1),
  "&:hover": {
    backgroundColor: "#3a54c6",
  },
}));

// Container for the component with enhanced padding
const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: "#FFFFFF",
  width: "100%",
  margin: "0 auto",
  [theme.breakpoints.up("sm")]: {
    width: "80%",
  },
}));

// Styled Ticket Box
const TicketBox = styled(Box)(({ theme }) => ({
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: theme.spacing(1, 2),
  display: "inline-block",
  marginRight: theme.spacing(1),
}));

// Component to display selected customer purchase details
const SelectedCustomers: React.FC<{
  selected: Customer[];
  onBack: () => void;
}> = ({ selected, onBack }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [purchaseDetails, setPurchaseDetails] = useState<PurchaseDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { i18n, t } = useTranslation();
  const language = i18n.language;
  // Fetch purchase details for selected customers
  useEffect(() => {
    const fetchPurchaseDetails = async () => {
      setLoading(true);
      try {
        const details = await Promise.all(
          selected.map(async (customer) => {
            const res = await sales_management_service.getEachOrder(
              Number(customer.id)
            );
            const data = res.data.info[0];

            const detail: PurchaseDetail = {
              customer: data.user_name,
              salesId: data.reserv_id,
              movieName:
                language === "en" ? data.eventName_en : data.eventName_am,
              movieSubtitle:
                language === "en" ? data.description_en : data.description_am,
              movieImage: data.movieImage,
              tickets: data.tickets.split(","),
              ticketCount: Number(data.ticketCount),
              time: data.dateTime.split(" ")[1],
              date: data.dateTime.split(" ")[0],
              qr_url:
                language === "en"
                  ? data.qr_url.replace("hy", "en")
                  : data.qr_url.replace("en", "hy"),
              hasSeatmap: data.hasSeatmap == "true" ? true : false,
            };
            return detail;
          })
        );
        setPurchaseDetails(details);
      } catch (error) {
        console.error(error);
        // Handle error appropriately
      } finally {
        setLoading(false);
      }
    };

    if (selected.length > 0) {
      fetchPurchaseDetails();
    }
  }, [selected, language]);

  // Function to handle print
  const handlePrint = (detail: PurchaseDetail) => {
    const printContent = document.getElementById(
      `print-section-${detail.salesId}`
    );
    if (printContent) {
      const WindowPrt = window.open("", "", "width=800,height=600");
      if (WindowPrt) {
        WindowPrt.document.write(`
          <html>
            <head>
              <title>Print Purchase Details</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                .print-container { border: 1px solid #ccc; padding: 20px; border-radius: 8px; }
                .header { display: flex; justify-content: space-between; align-items: center; }
                .customer-info { display: flex; align-items: center; margin-bottom: 20px; }
                .customer-info .icon { font-size: 40px; margin-right: 20px; }
                .qr-code { margin-bottom: 20px; }
                .movie-info { display: flex; align-items: flex-start; }
                .movie-image { width: 150px; height: 225px; background-color: #ddd; border-radius: 8px; overflow: hidden; margin-right: 20px; }
                .movie-image img { width: 100%; height: 100%; object-fit: cover; }
                .tickets { display: flex; align-items: center; margin-bottom: 10px; }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        WindowPrt.document.close();
        WindowPrt.focus();
        WindowPrt.print();
        WindowPrt.close();
      }
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      {purchaseDetails.map((detail) => (
        <Box key={detail.salesId} mb={4}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            {/* Header Section */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {t("Purchase Details")}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {t("Customer ID")}: {detail.salesId}
                </Typography>
              </Box>
              <Button
                variant="contained"
                style={{
                  backgroundColor: "#4D67EB",
                  color: "white",
                  padding: "8px 28px",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  textTransform: "none",
                }}
                onClick={() => handlePrint(detail)}
              >
                {t("Print")}
              </Button>
            </Box>

            {/* Customer Info Section */}
            <Box display="flex" alignItems="center" mb={3}>
              <MovieIcon sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h6">{detail.customer}</Typography>
              </Box>
            </Box>

            {/* QR Code and Sales Info Section */}
            <Box
              display="flex"
              flexDirection={isSmallScreen ? "column" : "row"}
              alignItems={"flex-start"}
              mb={3}
            >
              <Box sx={{ width: 150, height: 150, mb: isSmallScreen ? 2 : 0 }}>
                {detail.qr_url && <QRCode value={detail.qr_url} size={150} />}
              </Box>
              <Box
                sx={{
                  ml: isSmallScreen ? 0 : 4,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {t("Printing is unnecessary, enjoy your event!")}
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ mr: isSmallScreen ? 0 : 4 }}
                >
                  {t("SALES ID")}: {detail.salesId}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: "dotted", my: 3 }} />

            {/* Print Section */}
            <Box
              display="flex"
              flexDirection={"row"}
              alignItems={"flex-start"}
              mb={3}
              id={`print-section-${detail.salesId}`}
            >
              {/* Movie/Event Image */}
              <Box
                sx={{
                  width: 150,
                  height: 225,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#ddd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                {detail.movieImage ? (
                  <img
                    src={detail.movieImage}
                    alt={detail.movieName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <MovieIcon sx={{ fontSize: 60, color: "#888" }} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {detail.movieName}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {detail.movieSubtitle}
                  </Typography>
                </Box>
                <Box mb={2}>
                  {detail.hasSeatmap && (
                    <Box display="flex" alignItems="center" flexWrap="wrap">
                      {detail.tickets.map((ticket, index) => (
                        <TicketBox key={index}>
                          <Typography variant="body2">{ticket}</Typography>
                        </TicketBox>
                      ))}
                      <Typography variant="body2">
                        ({detail.ticketCount} {t("tickets")})
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Time and Date Section */}
                <Box>
                  <Typography variant="body1">
                    {detail.time} | {detail.date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

// Main CustomerListTable Component
const CustomerListTable: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { ordersList } = useSelector(
    (state: RootState) => state?.salesManagementReducer
  );
  const { t } = useTranslation();
  // Map ordersList to generate customers array
  const customers: Customer[] =
    ordersList?.map((order) => ({
      id: order.id,
      purchaseDate: order.po_date,
      customerName: order.user_name,
      email: order.user_email,
      purchaseReceipt: order.reserv_id,
      event_id: order.event_id,
      eventName_en: order.eventName_en,
    })) || [];

  // Generate event options for the filter dropdown
  const eventOptions = useMemo(() => {
    const eventMap = new Map<string, { id: string; name: string }>();
    ordersList?.forEach((order: any) => {
      if (!eventMap.has(order.event_id)) {
        eventMap.set(order.event_id, {
          id: order.event_id,
          name: order.eventName_en || "Unknown Event"
        });
      }
    });
    return Array.from(eventMap.values());
  }, [ordersList]);

  // Group customers by email and combine their orders
  const groupedCustomers: GroupedCustomer[] = useMemo(() => {
    const emailGroups = new Map<string, Customer[]>();
    
    // Group customers by email
    customers.forEach(customer => {
      const email = customer.email || 'no-email';
      if (!emailGroups.has(email)) {
        emailGroups.set(email, []);
      }
      emailGroups.get(email)!.push(customer);
    });
    
    // Convert groups to GroupedCustomer format
    const grouped = Array.from(emailGroups.entries()).map(([email, customerGroup]) => {
      // Sort customer group by purchase date (most recent first) to get the latest info
      const sortedGroup = customerGroup.sort((a, b) => 
        new Date(b.purchaseDate).getTime() - new Date(a.purchaseDate).getTime()
      );
      
      return {
        email,
        customerName: sortedGroup[0].customerName, // Use name from most recent order
        mostRecentPurchaseDate: sortedGroup[0].purchaseDate,
        orderNumbers: sortedGroup.map(c => c.purchaseReceipt),
        orderIds: sortedGroup.map(c => c.id),
      };
    });
    
    // Sort grouped customers by most recent purchase date (most recent first)
    return grouped.sort((a, b) => 
      new Date(b.mostRecentPurchaseDate).getTime() - new Date(a.mostRecentPurchaseDate).getTime()
    );
  }, [customers]);

  // State for selecting rows
  const [selected, setSelected] = useState<string[]>([]);

  // State for pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // State to handle view (table or selected customers) - kept for eye icon functionality
  const [isExporting, setIsExporting] = useState<boolean>(false);
  
  // State to handle individual customer view
  const [viewingCustomer, setViewingCustomer] = useState<GroupedCustomer | null>(null);

  // Date filter states
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Event filter state
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  // Filtered grouped customers based on date range and event selection
  const filteredCustomers = useMemo(() => {
    // First, filter customers by event if events are selected
    let customersToGroup = customers;
    if (selectedEvents.length > 0) {
      customersToGroup = customers.filter(c => selectedEvents.includes(c.event_id));
    }

    // Re-group the filtered customers by email
    const emailGroups = new Map<string, Customer[]>();
    customersToGroup.forEach(customer => {
      const email = customer.email || 'no-email';
      if (!emailGroups.has(email)) {
        emailGroups.set(email, []);
      }
      emailGroups.get(email)!.push(customer);
    });

    // Convert groups to GroupedCustomer format
    const filtered = Array.from(emailGroups.entries()).map(([email, customerGroup]) => {
      const sortedGroup = customerGroup.sort((a, b) =>
        new Date(b.purchaseDate).getTime() - new Date(a.purchaseDate).getTime()
      );

      return {
        email,
        customerName: sortedGroup[0].customerName,
        mostRecentPurchaseDate: sortedGroup[0].purchaseDate,
        orderNumbers: sortedGroup.map(c => c.purchaseReceipt),
        orderIds: sortedGroup.map(c => c.id),
      };
    });

    // Sort by most recent purchase date
    const sorted = filtered.sort((a, b) =>
      new Date(b.mostRecentPurchaseDate).getTime() - new Date(a.mostRecentPurchaseDate).getTime()
    );

    // Apply date filters
    return sorted.filter((c) => {
      if (!c.mostRecentPurchaseDate) return false;

      const purchaseDateObj = new Date(c.mostRecentPurchaseDate);

      // If fromDate is set, ensure c.mostRecentPurchaseDate >= fromDate
      if (fromDate && purchaseDateObj < new Date(fromDate)) {
        return false;
      }
      // If toDate is set, ensure c.mostRecentPurchaseDate <= toDate
      if (toDate && purchaseDateObj > new Date(toDate)) {
        return false;
      }
      return true;
    });
  }, [customers, fromDate, toDate, selectedEvents]);

  // Event handlers for date inputs
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
    setPage(0);
  };
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
    setPage(0);
  };

  // Event handler for event filter
  const handleEventChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedEvents(typeof value === 'string' ? value.split(',') : value);
    setPage(0);
  };

  // Handle selecting all customers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = filteredCustomers.flatMap((customer) => customer.orderIds);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // Handle selecting a single customer (all their orders)
  const handleSelect = (orderIds: string[]) => {
    // Check if any of the customer's orders are selected
    const isAnySelected = orderIds.some(id => selected.includes(id));
    
    let newSelected: string[] = [];
    
    if (isAnySelected) {
      // Remove all of this customer's order IDs from selection
      newSelected = selected.filter(id => !orderIds.includes(id));
    } else {
      // Add all of this customer's order IDs to selection
      newSelected = [...selected, ...orderIds];
    }

    setSelected(newSelected);
  };

  // Handle page change
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle Export button click - kept for potential future use with eye icon
  const handleExport = () => {
    setIsExporting(true);
  };

  // Handle Back button click in export screen
  const handleBack = () => {
    setIsExporting(false);
    setViewingCustomer(null);
  };
  
  // Handle eye icon click to view individual customer
  const handleViewCustomer = (customer: GroupedCustomer) => {
    setViewingCustomer(customer);
  };

  // Excel export functionality
  const exportToExcel = () => {
    // Determine which customers to export
    let customersToExport: GroupedCustomer[] = [];
    
    if (selected.length > 0) {
      // Export selected customers only
      customersToExport = filteredCustomers.filter((customer) =>
        customer.orderIds.some(id => selected.includes(id))
      );
    } else {
      // Export all filtered customers
      customersToExport = filteredCustomers;
    }

    // Prepare data for export
    const exportData = customersToExport.map((customer) => ({
      'Customer Name': customer.customerName,
      'Email': customer.email,
      'Most Recent Purchase Date': customer.mostRecentPurchaseDate,
      'Total Orders': customer.orderIds.length
    }));

    // Create CSV content
    const headers = ['Customer Name', 'Email', 'Most Recent Purchase Date', 'Total Orders'];
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape commas and quotes in CSV
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customers-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Show success message
    if (customersToExport.length > 0) {
      alert(`Successfully exported ${customersToExport.length} unique customers to Excel format.`);
    } else {
      alert('No customers to export. Please select customers or adjust your filters.');
    }
  };

  // Determine if all filtered rows are selected
  const allOrderIds = filteredCustomers.flatMap(customer => customer.orderIds);
  const isAllSelected =
    selected.length > 0 &&
    selected.length === allOrderIds.length &&
    allOrderIds.length > 0;

  // Get selected customer objects - convert back to Customer format for export
  const selectedCustomers: Customer[] = filteredCustomers
    .filter((groupedCustomer) =>
      groupedCustomer.orderIds.some(id => selected.includes(id))
    )
    .flatMap((groupedCustomer) =>
      groupedCustomer.orderIds
        .filter(id => selected.includes(id))
        .map(id => {
          // Find the original customer data for this order ID
          const originalCustomer = customers.find(c => c.id === id);
          return originalCustomer || {
            id,
            purchaseDate: groupedCustomer.mostRecentPurchaseDate,
            customerName: groupedCustomer.customerName,
            email: groupedCustomer.email,
            purchaseReceipt: groupedCustomer.orderNumbers.find((_, index) =>
              groupedCustomer.orderIds[index] === id
            ) || '',
            event_id: '',
            eventName_en: '',
          };
        })
    );

  // Get viewing customer objects - convert to Customer format for individual view
  const viewingCustomerData: Customer[] = viewingCustomer ?
    viewingCustomer.orderIds.map(id => {
      const originalCustomer = customers.find(c => c.id === id);
      return originalCustomer || {
        id,
        purchaseDate: viewingCustomer.mostRecentPurchaseDate,
        customerName: viewingCustomer.customerName,
        email: viewingCustomer.email,
        purchaseReceipt: viewingCustomer.orderNumbers.find((_, index) =>
          viewingCustomer.orderIds[index] === id
        ) || '',
        event_id: '',
        eventName_en: '',
      };
    }) : [];

  // If viewing individual customer or exporting, show the customer details component
  if (isExporting || viewingCustomer) {
    const customersToShow = isExporting ? selectedCustomers : viewingCustomerData;
    const titleText = isExporting ? t("Back To Customers List") : `${t("Back To Customers List")} - ${viewingCustomer?.customerName}`;
    
    return (
      <Box
        display="flex"
        minHeight="90vh"
        width="100vw"
        bgcolor="#f0f2f5"
        padding={2}
        justifyContent="center"
        alignItems="flex-start"
      >
        <Container>
          <Box display="flex" alignItems="center" mb={2}>
            <BackButton onClick={handleBack} style={{ marginBottom: 1 }}>
              <ArrowBackIcon />
            </BackButton>
            <Typography
              variant={isSmallScreen ? "h6" : "h5"}
              gutterBottom
              sx={{ ml: isSmallScreen ? 1 : 2 }}
            >
              {titleText}
            </Typography>
          </Box>
          <SelectedCustomers selected={customersToShow} onBack={handleBack} />
        </Container>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      minHeight="90vh"
      width="100vw"
      bgcolor="#f0f2f5"
      padding={2}
      justifyContent="center"
      alignItems="flex-start"
    >
      <Container>
        {/* Title & Filter Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
        >
          <Box>
            <Typography variant="h5" gutterBottom>
              {t("Customers List")}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {t("You have")} {groupedCustomers.length.toLocaleString()}{" "}
              {t("total customers")} ({customers.length} {t("total orders")})
            </Typography>
          </Box>

          {/* Filters + Export Button */}
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            justifyContent={{ xs: "center", sm: "flex-end" }}
          >
            {/* Date Filters */}
            <TextField
              label={t("From Date")}
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("To Date")}
              type="date"
              value={toDate}
              onChange={handleToDateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* Event Filter */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="event-select-label">{t("Events")}</InputLabel>
              <Select
                labelId="event-select-label"
                multiple
                value={selectedEvents}
                label={t("Events")}
                onChange={handleEventChange}
                renderValue={(selected) =>
                  selected.length === 0
                    ? t("All Events")
                    : `${selected.length} ${selected.length === 1 ? t("event") : t("events")}`
                }
              >
                {eventOptions.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    <ListItemText primary={event.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={exportToExcel}
              startIcon={<FileDownloadIcon />}
              style={{
                borderColor: "#4D67EB",
                color: "#4D67EB",
                padding: "8px 20px",
                borderRadius: "6px",
                fontSize: "1rem",
                textTransform: "none",
              }}
            >
              {selected.length > 0 ? t("Export Selected to Excel") : t("Export All to Excel")}
            </Button>
          </Box>
        </Box>

        {/* Table Section */}
        <Box mt={4}>
          <Paper elevation={0}>
            <TableContainer
              sx={{
                overflowX: "auto", // Enable horizontal scrolling on small screens
              }}
            >
              <Table
                sx={{
                  minWidth: 650, // Ensure table has a minimum width
                }}
                size={isSmallScreen ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                      <Checkbox
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < filteredCustomers.length
                        }
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        inputProps={{
                          "aria-label": "select all customers",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Typography fontWeight="bold">
                        {t("CUSTOMER NAME")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Typography fontWeight="bold">{t("EMAIL")}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Typography>{t("PURCHASE DATE")}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Typography>{t("ORDER NUMBERS")}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Typography>{t("Actions")}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((customer) => {
                      const isAnySelected = customer.orderIds.some(id => selected.includes(id));
                      return (
                        <TableRow
                          key={customer.email}
                          hover
                          role="checkbox"
                          aria-checked={isAnySelected}
                          selected={isAnySelected}
                          onClick={() => handleSelect(customer.orderIds)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isAnySelected}
                              onChange={() => handleSelect(customer.orderIds)}
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{
                                "aria-labelledby": `customer-checkbox-${customer.email}`,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "none" }}>
                            {customer.customerName}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "none" }}>
                            {customer.email}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "none" }}>
                            {customer.mostRecentPurchaseDate}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "none", maxWidth: "200px" }}>
                            <Box component="span" sx={{ 
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {customer.orderNumbers.join(', ')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "none" }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewCustomer(customer);
                              }}
                              sx={{ 
                                color: "#4D67EB",
                                "&:hover": {
                                  backgroundColor: "rgba(77, 103, 235, 0.1)"
                                }
                              }}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredCustomers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                ".MuiTablePagination-toolbar": {
                  flexDirection: isSmallScreen ? "column" : "row",
                  alignItems: isSmallScreen ? "flex-start" : "center",
                  gap: isSmallScreen ? theme.spacing(1) : 0,
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  marginRight: isSmallScreen ? 0 : theme.spacing(2),
                },
              }}
            />
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerListTable;
